'use client';

import { useEffect, useRef } from 'react';
import { referralLinkBannedCharactersRegex } from 'src/constants/constants';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { decodeName } from '@velocity-exchange/sdk';
import useVelocityClient from './useVelocityClient';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useWalletIsConnected from './useWalletIsConnected';
import useAccountData from './useAccountData';

export default function useReferrerNameAccounts() {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const accountsStoreSet = useVelocityAccountStore((s) => s.set);
	const currentPublicKey = useAccountData()?.authority;
	const dataLoadedForWallet = useRef(currentPublicKey);

	const referrerAccountsLoading = useVelocityAccountStore(
		(s) => s.referrerNameAccounts.loading
	);
	const initialReferrerNameAccountsLoaded = useVelocityAccountStore(
		(s) => s.referrerNameAccounts.initialReferrerNameAccountsLoaded
	);
	const userAccountNotInitialized = useVelocityAccountStore(
		(s) => s.userAccountNotInitialized
	);
	const referrerNameAccounts = useVelocityAccountStore(
		(s) => s.referrerNameAccounts
	);

	/**
	 * Reset referrer name accounts state when wallet disconnects
	 */
	useWalletIsConnected({
		onDisconnectedCallback: () => {
			accountsStoreSet((s) => {
				s.referrerNameAccounts.accounts = [];
				s.referrerNameAccounts.loading = false;
				s.referrerNameAccounts.initialReferrerNameAccountsLoaded = false;
			});
		},
	});

	/**
	 * Fetches referrer names created by current authority
	 * This should be done once on initial app load, and once each time a user successfully creates a referral code from the UI
	 */
	const fetchReferrerNameAccounts = async () => {
		if (
			!currentPublicKey ||
			!velocityClientIsReady ||
			referrerAccountsLoading
		) {
			return;
		}

		// Just set empty state + loaded if velocity account is not initialized
		if (userAccountNotInitialized) {
			accountsStoreSet((s) => {
				s.referrerNameAccounts.accounts = [];
				s.referrerNameAccounts.loading = false;
				s.referrerNameAccounts.initialReferrerNameAccountsLoaded = true;
			});
			return;
		}

		accountsStoreSet((s) => {
			s.referrerNameAccounts.loading = true;
		});

		const fetchedReferrerNameAccounts =
			await velocityClient?.getReferrerNameAccountsForAuthority(
				currentPublicKey
			);

		const decodedAccounts =
			fetchedReferrerNameAccounts?.map((account) => {
				return {
					...account,
					decodedName: decodeName(account.name).replace(
						referralLinkBannedCharactersRegex,
						''
					),
				};
			}) ?? [];

		accountsStoreSet((s) => {
			s.referrerNameAccounts.accounts = decodedAccounts;
			s.referrerNameAccounts.loading = false;
			s.referrerNameAccounts.initialReferrerNameAccountsLoaded = true;
		});
	};

	useEffect(() => {
		if (!initialReferrerNameAccountsLoaded && !referrerAccountsLoading) {
			fetchReferrerNameAccounts();
		}
	}, [
		userAccountNotInitialized,
		velocityClientIsReady,
		referrerAccountsLoading,
		initialReferrerNameAccountsLoaded,
	]);

	useEffect(() => {
		if (
			currentPublicKey &&
			(!dataLoadedForWallet.current ||
				!dataLoadedForWallet.current?.equals(currentPublicKey))
		) {
			fetchReferrerNameAccounts();
		}
	}, [currentPublicKey]);

	return {
		referrerNameAccounts,
		fetchReferrerNameAccounts,
	};
}
